import { Button } from "@portal/BaseComponents/Button/AntButton";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Form, FormItem, useForm } from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { Input } from "@portal/BaseComponents/Input/AntInput";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { ApiError, bad_request_status } from "@portal/lib/ky";
import { Style } from "@portal/lib/style/style";
import { useSendOtp } from "@portal/queries/login";
import {
  countDownAtom,
  resetTimerPredicate,
} from "@portal/store/countdown/countdown";
import { portalPreferencesAtom } from "@portal/store/preferences/preferences";
import { DefaultErrorResponse, LOGIN_ERROR_STATUS } from "@portal/types/common";
import { LoginDTO } from "@portal/types/entities/login";
import { emailValidation, fieldIsRequired } from "@portal/util/validation";
import { useAtom, useSetAtom } from "jotai";
import { useState } from "react";
import { AlertStatus } from "./AlterInfo";
import { StyledCountdown } from "./EnterPin/style";
import EnterPin from "./EnterPin/EnterPin";

const StyledTextButton = Style(Button, {
  "&.ant-btn": {
    padding: 0,

    "&:hover": {
      background: "none",
    },
  },
});
const t = {
  title: "Sign in using PIN",
  label: {
    email: "Email Address",
    edit: "Edit email address",
  },
  generatePin: "Generate PIN",
};

interface PinLoginProps {
  updateStatus: (status?: AlertStatus) => void;
  status?: AlertStatus;
  handleSubmit?: () => void;
  handleLoginErrorStatus: (status?: LOGIN_ERROR_STATUS) => void;
  loginErrorStatus?: LOGIN_ERROR_STATUS;
}

function PinLogin(props: PinLoginProps) {
  const [editMode, setEditMode] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [disabled, setIsDisabled] = useState(true);
  const [countDown, setCountDown] = useAtom(countDownAtom);
  const [form] = useForm<Pick<LoginDTO, "email">>();
  const setEmail = useSetAtom(portalPreferencesAtom);
  const sendOTP = useSendOtp();

  async function handleError(
    error: ApiError<DefaultErrorResponse<LOGIN_ERROR_STATUS>>
  ) {
    setIsValid(false);
    if (error.response.status !== bad_request_status) {
      return;
    }
    const error_status = await error.response.json();
    props.handleLoginErrorStatus(error_status.error);

    if (["OTP_WAIT_TIME", "OTP_NOT_FOUND"].includes(error_status.error)) {
      setEditMode(true);
      setIsValid(true);
      return;
    }

    if (error_status.error !== "OTP_NOT_MATCHING") {
      return;
    }
    props.handleLoginErrorStatus("OTP_NOT_MATCHING");
    setIsValid(true);
  }
  function handleSubmit() {
    form.validateFields().then((value) => {
      setEmail((pref) => {
        return { ...pref, email: value.email };
      });
      sendOTP
        .mutateAsync({ ...value })
        .then(() => {
          setIsValid(true);
          setEditMode(true);
          props.updateStatus("info");
          props.handleLoginErrorStatus(undefined);
          setCountDown(resetTimerPredicate);
          setIsDisabled(true);
        })
        .catch(handleError);
    });
  }

  const EditButton = (
    <StyledTextButton
      size="small"
      type="text"
      onClick={() => {
        setEditMode(false);
        setIsValid(false);
        props.updateStatus(undefined);
        props.handleLoginErrorStatus(undefined);
      }}
    >
      {t.label.edit}
    </StyledTextButton>
  );

  const Title = (
    <TypographyText strong fs="16">
      {t.title}
    </TypographyText>
  );

  const EditEmail = (
    <Flex vertical align="start" space="8">
      <Flex vertical space="4" align="start">
        <TypographyText fs="14">{t.label.email}</TypographyText>
        <StyledFormItem
          name="email"
          rules={[...emailValidation, ...fieldIsRequired]}
          preserve
          lh="l1"
          initialValue={form.getFieldValue("email")}
        >
          <TypographyText strong fs="14">
            {form.getFieldValue("email")}
          </TypographyText>
        </StyledFormItem>
      </Flex>
      {EditButton}
    </Flex>
  );

  const SubmitButton = (
    <Button size="large" htmlType="submit" loading={sendOTP.isLoading}>
      {t.generatePin}
    </Button>
  );

  const ResendOTP = (
    <Button
      type="text"
      onClick={handleSubmit}
      style={{ padding: 0 }}
      disabled={disabled}
    >
      Regenerate OTP
    </Button>
  );
  const SessionCount = (
    <StyledCountdown
      value={countDown.waitTime}
      format="mm:ss"
      onFinish={() => {
        setIsDisabled(false);
      }}
    />
  );

  const ResendArea = (
    <Flex align="center">
      {SessionCount}
      {ResendOTP}
    </Flex>
  );

  const Email = (
    <FormItem
      fieldId="email_address"
      required
      name="email"
      label={t.label.email}
      style={{ width: "100%" }}
      rules={[...emailValidation, ...fieldIsRequired]}
      preserve
      initialValue={form.getFieldValue("email")}
    >
      <Input
        placeholder="Enter your Email Address..."
        style={{ height: "40px" }}
      />
    </FormItem>
  );

  const EmailForm = (
    <Flex vertical style={{ width: "100%" }} space="20" align="start">
      {Title}
      <Form
        form={form}
        layout="vertical"
        style={{ width: "100%" }}
        onFinish={handleSubmit}
        preserve
      >
        <Flex
          vertical
          style={{ width: "100%" }}
          align="start"
          space={props.status === "warning" && !editMode ? "24" : "16"}
        >
          {editMode ? EditEmail : Email}
          {!editMode || props.status === "warning" ? SubmitButton : null}
        </Flex>
      </Form>
    </Flex>
  );

  const PinArea = (
    <EnterPin
      updateStatus={props.updateStatus}
      countDownValue={countDown.expireTime}
      handleError={handleError}
      error={props.loginErrorStatus === "OTP_NOT_MATCHING"}
      errorStatus={props.loginErrorStatus}
      extra={ResendArea}
    />
  );
  return (
    <Flex style={{ width: "100%" }} vertical space="24" align="start">
      {EmailForm}
      {isValid && props.status !== "warning" ? PinArea : null}
    </Flex>
  );
}

export { PinLogin };
